/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect, useState } from 'react';
import StoryblokClient from 'storyblok-js-client';
import { DateService } from '@rocheglobal/component-library/src/services/date/index';
import { Props } from '../types';
import { StoryblokService } from '../../services';
import { MultiLinkData } from '../props/multi-link';
import { AssetData } from '../props/asset';

type DynamicEventTeaserFields = {
  title: string;
  number_of_events: number;
  hide_image: boolean;
  fallback_image: AssetData;
  fallback_title: string;
  fallback_description: string;
  fallback_link: MultiLinkData;
  fallback_link_text: string;
  transition_time: number;
}
export const DynamicEventTeaser = ({ blok, slot }: Props<DynamicEventTeaserFields>):
JSX.Element => {
  const [events, setEvents] = useState([]);
  const [images, setImages] = useState([]);
  const teaserUrl = blok.fallback_link?.story?.url ? `/${blok.fallback_link?.story?.url}` : blok.fallback_link?.url;

  const fallbackTeaser = {
    image: blok.fallback_image as unknown as string,
    text: blok.fallback_description,
    'teaser-link-url': teaserUrl,
  };
  let fallbackLink;
  if (teaserUrl) {
    fallbackLink = {
      url: teaserUrl,
      text: blok.fallback_link_text || 'zur Event Seite',
      icon: 'chevron-right',
    };
  }
  const storyToEventTeaser = (story) => {
    if (!story || !story.content) { return {}; }
    return {
      city: story.content.city,
      country: story.content.country,
      'end-date': story.content.end_date,
      'start-date': story.content.start_date,
      'title-text': story.content.teaser_title,
      'show-date-on-top': true,
      'show-calendar-icon': true,
      image: story.content.teaser_image,
      'teaser-link-url': teaserUrl,
    };
  };

  const fetchStories = async () => {
    const storyblokJsClient = new StoryblokClient({
      accessToken: StoryblokService.getConfig().options.accessToken as string,
    });
    const stories = await storyblokJsClient.getStories({
      per_page: blok.number_of_events || 3,
      sort_by: 'content.start_date:asc',
      filter_query: {
        component: {
          in: 'page-event',
        },
        authorized_roles: {
          is: 'empty_array',
        },
        start_date: {
          gt_date: DateService.getDateTimeValue(new Date().toISOString()),
        },
      },
    });
    const finalEvents = stories.data.stories.map(storyToEventTeaser);
    setEvents(finalEvents);
    const finalImages: { image: string; href: string }[] = finalEvents
      .filter((e) => e.image && e.image.filename)
      .map((e) => ({
        image: JSON.stringify({ ...e.image, src: e.image.filename }),
        href: e['teaser-link-url'],
      }));

    if (finalImages.length === 0 && blok.fallback_image && blok.fallback_image.filename) {
      finalImages.push({
        image: JSON.stringify({ ...blok.fallback_image, src: blok.fallback_image.filename }),
        href: teaserUrl,
      });
    }
    setImages(finalImages);
  };

  useEffect(() => {
    setImages([]);
    fetchStories();
  }, [blok]);

  const TeaserLinkElement = (props: { image: { image; href } }) => (props.image.href ? (
    <a href={props.image.href}>
      <roche-image src={props.image.image} />
    </a>) : (
    <div>
      <roche-image src={props.image.image} />
    </div>
  ));

  const slider = (): JSX.Element => (
    <roche-slider per-page={1} auto-play-interval={blok.transition_time || 3} hide-controls={true}>
      {images.map((image, i) => <roche-slider-item key={i}>
        <TeaserLinkElement image={image} />
      </roche-slider-item>)}
    </roche-slider>
  );

  return (
    <section slot={slot}>
      {!blok.hide_image && images && images.length > 1 && slider()}
      {!blok.hide_image && images && images.length === 1 && (
        <TeaserLinkElement image={images[0]} />
      )}
      <roche-headline
        level={3}
        headline={events?.length === 0 ? blok?.fallback_title : blok?.title}
      />
      {events.map((e, i) => <roche-event-teaser {...e} key={i} />)}

      {events?.length === 0 && (<roche-event-teaser {...fallbackTeaser}>
        {fallbackLink && <roche-generic-link slot="link" {...fallbackLink} />}
      </roche-event-teaser>)}
    </section>
  );
};
